(function() {
  'use strict';

  if (typeof window.RolexQuiz !== 'undefined') {
    console.warn('RolexQuiz is already defined. Skipping redefinition.');
    return;
  }

class RolexQuiz {
  constructor(sectionId, dataUrl) {
    this.sectionId = sectionId;
    this.dataUrl = dataUrl;
    this.form = document.getElementById(`quiz-form-${sectionId}`);
    this.resultsDiv = document.getElementById(`quiz-results-${sectionId}`);
    this.questionPool = [];
    this.selectedQuestions = [];
    this.totalQuestions = 7;
    this.currentQuestionIndex = 0;
    this.userAnswers = new Array(this.totalQuestions).fill(null);
    this.answeredQuestions = new Set();
    this.submittedQuestions = new Set();
    this.questionSubmitted = false;

    this.tiers = {
      gold: { min: 6, emoji: '🥇', title: 'Gold Medal - Reference Expert!' },
      silver: { min: 4, emoji: '🥈', title: 'Silver Medal - Rolex Collector!' },
      bronze: { min: 0, emoji: '🥉', title: 'Bronze Medal - Watch Enthusiast!' }
    };

    this.init();
  }

  async init() {
    if (!this.form || !this.resultsDiv) {
      console.error('Quiz elements not found');
      return;
    }

    await this.loadQuestions();
    this.selectRandomQuestions();
    this.showQuestion(0);
    this.attachEventListeners();

    // Track quiz start
    this.trackEvent('quiz_started', {
      quiz_name: 'rolex_reference_numbers'
    });
  }

  async loadQuestions() {
    try {
      const response = await fetch(this.dataUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.questions)) {
        throw new Error('Invalid quiz data structure');
      }

      this.questionPool = data.questions.filter(q => {
        return q.id &&
               typeof q.text === 'string' &&
               q.text.trim().length > 0 &&
               Array.isArray(q.options) &&
               q.options.length > 0 &&
               q.options.every(opt => typeof opt.text === 'string' && typeof opt.correct === 'boolean') &&
               typeof q.explanation === 'string';
      });

      if (this.questionPool.length === 0) {
        throw new Error('No valid questions found in quiz data');
      }

    } catch (error) {
      console.error('Failed to load quiz questions:', error);
      this.displayErrorMessage('Unable to load quiz. Please refresh the page or contact support.');
    }
  }

  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  selectRandomQuestions() {
    if (!this.questionPool || this.questionPool.length === 0) {
      console.error('No questions available');
      this.displayErrorMessage('Unable to load quiz questions. Please refresh the page.');
      return;
    }

    const questionsNeeded = Math.min(this.totalQuestions, this.questionPool.length);
    const shuffled = this.shuffle([...this.questionPool]);
    this.selectedQuestions = shuffled.slice(0, questionsNeeded);
    this.totalQuestions = this.selectedQuestions.length;
  }

  escapeHtml(unsafe) {
    if (typeof unsafe !== 'string') {
      return '';
    }
    const div = document.createElement('div');
    div.textContent = unsafe;
    return div.innerHTML;
  }

  trackEvent(eventName, eventParams = {}) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, eventParams);
    }
  }

  displayErrorMessage(message) {
    const questionsContainer = this.form?.querySelector('.quiz-questions-container');
    if (questionsContainer) {
      questionsContainer.innerHTML = `<p style="color: #d9534f; text-align: center; padding: 20px;">${this.escapeHtml(message)}</p>`;
    }
  }

  showQuestion(index) {
    this.currentQuestionIndex = index;
    const questionsContainer = this.form.querySelector('.quiz-questions-container');
    const progressIndicator = this.form.querySelector('.quiz-progress');
    const backBtn = this.form.querySelector('.quiz-back-btn');
    const nextBtn = this.form.querySelector('.quiz-next-btn');

    if (!questionsContainer) return;

    const q = this.selectedQuestions[index];
    const questionNum = index + 1;

    const imageHtml = (q.imageUrl && /^https?:\/\//i.test(q.imageUrl))
      ? `<img src="${this.escapeHtml(q.imageUrl)}" alt="Question ${questionNum}" class="question-image" loading="lazy">`
      : '';

    questionsContainer.innerHTML = `
      <div class="quiz-question" data-question="${questionNum}" data-question-id="${this.escapeHtml(q.id)}">
        <h3 class="question-text">${questionNum}. ${this.escapeHtml(q.text)}</h3>
        ${imageHtml}
        <div class="quiz-options">
          ${q.options.map((opt, optIndex) => `
            <label class="quiz-option">
              <input type="radio" name="q${questionNum}" value="${opt.correct ? 'correct' : 'incorrect'}" ${optIndex === 0 ? 'required' : ''} data-option-index="${optIndex}" />
              <span>${this.escapeHtml(opt.text)}</span>
            </label>
          `).join('')}
        </div>
        <div class="quiz-explanation" style="display: none;">
          <p class="explanation-text">${this.escapeHtml(q.explanation)}</p>
        </div>
      </div>
    `;

    if (progressIndicator) {
      progressIndicator.textContent = `Question ${questionNum} of ${this.totalQuestions}`;
    }

    if (backBtn) {
      backBtn.style.display = index === 0 ? 'none' : 'inline-block';
    }

    const wasSubmitted = this.submittedQuestions.has(index);

    if (nextBtn) {
      if (index === this.totalQuestions - 1) {
        nextBtn.textContent = 'Submit Quiz';
        nextBtn.classList.add('is-submit');
      } else if (wasSubmitted) {
        nextBtn.textContent = 'Continue';
        nextBtn.classList.remove('is-submit');
      } else {
        nextBtn.textContent = 'Next';
        nextBtn.classList.remove('is-submit');
      }
      nextBtn.disabled = !this.answeredQuestions.has(index);
    }

    if (this.userAnswers[index] !== null) {
      const savedAnswer = this.userAnswers[index];
      const radioInputs = questionsContainer.querySelectorAll('input[type="radio"]');
      radioInputs.forEach((radio, radioIndex) => {
        if (radioIndex === savedAnswer.optionIndex) {
          radio.checked = true;
          radio.closest('.quiz-option').classList.add('selected');
        }

        if (wasSubmitted) {
          radio.disabled = true;
          const label = radio.closest('.quiz-option');
          if (radio.value === 'correct') {
            label.classList.add('correct-answer');
          } else if (radio.checked && radio.value === 'incorrect') {
            label.classList.add('incorrect-answer');
          }
        }
      });

      if (wasSubmitted) {
        const explanation = questionsContainer.querySelector('.quiz-explanation');
        if (explanation) {
          explanation.style.display = 'block';
        }
      }
    }

    this.addRadioFeedback();
  }

  attachEventListeners() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
    });

    const nextBtn = this.form.querySelector('.quiz-next-btn');
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (nextBtn.classList.contains('is-submit')) {
          this.handleSubmit();
        } else if (this.questionSubmitted) {
          this.nextQuestion();
        } else {
          this.submitCurrentQuestion();
        }
      });
    }

    const backBtn = this.form.querySelector('.quiz-back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousQuestion();
      });
    }

    const retakeBtn = this.resultsDiv.querySelector('.quiz-retake-btn');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', () => {
        this.resetQuiz();
      });
    }
  }

  nextQuestion() {
    if (this.currentQuestionIndex < this.totalQuestions - 1) {
      const nextIndex = this.currentQuestionIndex + 1;
      this.questionSubmitted = this.submittedQuestions.has(nextIndex);
      this.showQuestion(nextIndex);
    }
  }

  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      const prevIndex = this.currentQuestionIndex - 1;
      this.questionSubmitted = this.submittedQuestions.has(prevIndex);
      this.showQuestion(prevIndex);
    }
  }

  submitCurrentQuestion() {
    const question = this.form.querySelector('.quiz-question');
    if (!question) return;

    const allOptions = question.querySelectorAll('input[type="radio"]');

    allOptions.forEach(radio => {
      radio.disabled = true;
      const label = radio.closest('.quiz-option');

      if (radio.value === 'correct') {
        label.classList.add('correct-answer');
      } else if (radio.checked && radio.value === 'incorrect') {
        label.classList.add('incorrect-answer');
      }
    });

    const explanation = question.querySelector('.quiz-explanation');
    if (explanation) {
      explanation.style.display = 'block';
    }

    const nextBtn = this.form.querySelector('.quiz-next-btn');
    if (nextBtn) {
      if (this.currentQuestionIndex === this.totalQuestions - 1) {
        nextBtn.textContent = 'Submit Quiz';
      } else {
        nextBtn.textContent = 'Continue';
      }
    }

    this.submittedQuestions.add(this.currentQuestionIndex);
    this.questionSubmitted = true;
  }

  addRadioFeedback() {
    const radioInputs = this.form.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const question = e.target.closest('.quiz-question');
        question.querySelectorAll('.quiz-option').forEach(opt => {
          opt.classList.remove('selected');
        });
        e.target.closest('.quiz-option').classList.add('selected');

        const optionIndex = parseInt(e.target.dataset.optionIndex);
        const isCorrect = e.target.value === 'correct';

        this.userAnswers[this.currentQuestionIndex] = {
          optionIndex: optionIndex,
          isCorrect: isCorrect
        };

        this.answeredQuestions.add(this.currentQuestionIndex);

        const nextBtn = this.form.querySelector('.quiz-next-btn');
        if (nextBtn) {
          nextBtn.disabled = false;
        }
      });
    });
  }

  handleSubmit() {
    const score = this.calculateScore();
    this.showResults(score);
  }

  calculateScore() {
    let correctAnswers = 0;

    this.userAnswers.forEach(answer => {
      if (answer && answer.isCorrect) {
        correctAnswers++;
      }
    });

    return correctAnswers;
  }


  getMedalTier(score) {
    if (score >= this.tiers.gold.min) {
      return this.tiers.gold;
    } else if (score >= this.tiers.silver.min) {
      return this.tiers.silver;
    } else {
      return this.tiers.bronze;
    }
  }

  getResultMessage(score) {
    const percentage = Math.round((score / this.totalQuestions) * 100);

    const messages = {
      gold: `Outstanding! You scored ${score}/${this.totalQuestions} (${percentage}%). You can decode Rolex references like a seasoned collector. The GMT-Master II "Pepsi" vs "Batman" holds no mystery for you!`,
      silver: `Well done! You scored ${score}/${this.totalQuestions} (${percentage}%). You have solid knowledge of Rolex reference numbers. A few more hours studying those catalogs and you'll be an expert!`,
      bronze: `Good start! You scored ${score}/${this.totalQuestions} (${percentage}%). You're building your Rolex knowledge. Keep learning—those reference numbers will make perfect sense soon!`
    };

    if (score >= this.tiers.gold.min) return messages.gold;
    if (score >= this.tiers.silver.min) return messages.silver;
    return messages.bronze;
  }

  getShareText(tier, score) {
    const percentage = Math.round((score / this.totalQuestions) * 100);
    const shareTexts = {
      gold: `I scored ${score}/${this.totalQuestions} (${percentage}%) ${tier.emoji} on the Rolex Reference Numbers Quiz! Can you decode like a pro?`,
      silver: `I got ${score}/${this.totalQuestions} ${tier.emoji} on the Rolex Reference Quiz! Think you know your references?`,
      bronze: `Testing my Rolex knowledge... ${score}/${this.totalQuestions} ${tier.emoji} Can you beat my score?`
    };

    if (score >= this.tiers.gold.min) return shareTexts.gold;
    if (score >= this.tiers.silver.min) return shareTexts.silver;
    return shareTexts.bronze;
  }

  showResults(score) {
    const tier = this.getMedalTier(score);
    const message = this.getResultMessage(score);
    const shareText = this.getShareText(tier, score);
    const percentage = Math.round((score / this.totalQuestions) * 100);

    // Track quiz completion
    const tierName = score >= this.tiers.gold.min ? 'gold' :
                     score >= this.tiers.silver.min ? 'silver' : 'bronze';

    this.trackEvent('quiz_completed', {
      quiz_name: 'rolex_reference_numbers',
      score: score,
      total_questions: this.totalQuestions,
      medal_tier: tierName,
      percentage: percentage
    });

    const medalIcon = this.resultsDiv.querySelector('.medal-icon');
    const resultsTitle = this.resultsDiv.querySelector('.results-title');
    const resultsScore = this.resultsDiv.querySelector('.results-score');
    const resultsMessage = this.resultsDiv.querySelector('.results-message');

    if (medalIcon) medalIcon.textContent = tier.emoji;
    if (resultsTitle) resultsTitle.textContent = tier.title;
    if (resultsScore) resultsScore.textContent = `You scored ${score} out of ${this.totalQuestions}`;
    if (resultsMessage) resultsMessage.textContent = message;

    const shareButtons = this.resultsDiv.querySelector('.quiz-share-buttons');
    if (shareButtons) {
      let currentUrl = window.location.href;
      currentUrl = currentUrl.split('?')[0].split('#')[0];

      if (!currentUrl.startsWith(window.location.origin)) {
        currentUrl = window.location.origin;
      }

      const quizUrl = encodeURIComponent(currentUrl);
      const encodedText = encodeURIComponent(shareText);

      shareButtons.innerHTML = `
        <h4>Share Your Score:</h4>
        <div class="share-button-group">
          <a href="https://twitter.com/intent/tweet?text=${encodedText}&url=${quizUrl}"
             target="_blank"
             rel="noopener"
             class="share-btn twitter"
             onclick="if(typeof window.gtag === 'function') window.gtag('event', 'share', {method: 'twitter', content_type: 'quiz_result', item_id: 'rolex_reference_quiz'});">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            Tweet
          </a>
          <a href="https://www.linkedin.com/sharing/share-offsite/?url=${quizUrl}&summary=${encodedText}"
             target="_blank"
             rel="noopener"
             class="share-btn linkedin"
             onclick="if(typeof window.gtag === 'function') window.gtag('event', 'share', {method: 'linkedin', content_type: 'quiz_result', item_id: 'rolex_reference_quiz'});">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            Share
          </a>
          <a href="https://www.facebook.com/sharer/sharer.php?u=${quizUrl}&quote=${encodedText}"
             target="_blank"
             rel="noopener"
             class="share-btn facebook"
             onclick="if(typeof window.gtag === 'function') window.gtag('event', 'share', {method: 'facebook', content_type: 'quiz_result', item_id: 'rolex_reference_quiz'});">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Share
          </a>
        </div>
      `;
    }

    this.form.style.display = 'none';
    this.resultsDiv.style.display = 'block';

    this.resultsDiv.scrollIntoView({ behavior: 'smooth', block: 'start' });

    setTimeout(() => {
      this.resultsDiv.focus();
    }, 300);

    setTimeout(() => {
      this.resultsDiv.classList.add('show');
    }, 100);
  }

  resetQuiz() {
    this.currentQuestionIndex = 0;
    this.userAnswers = new Array(this.totalQuestions).fill(null);
    this.answeredQuestions = new Set();
    this.submittedQuestions = new Set();
    this.questionSubmitted = false;

    this.resultsDiv.style.display = 'none';
    this.resultsDiv.classList.remove('show');
    this.form.style.display = 'block';

    this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });

    this.selectRandomQuestions();
    this.showQuestion(0);
  }
}

  window.RolexQuiz = RolexQuiz;

})();
